import moment from 'moment-timezone';
import { convertSecToTimeFormat } from './time';

// offTimeRequests.totalHours holds MINUTES despite the name (see the PTO admin
// route and the analytics catalog), so seconds are minutes * 60.
const SECONDS_PER_PTO_UNIT = 60;

/**
 * Weekly totals for a member, bucketed in the given timezone.
 *
 * Mirrors the aggregation the Timesheets (View Activity) page performs so the
 * profile's week strip and the detailed page agree. A day's total is its
 * tracked time, plus every manual entry that *starts* inside that day, plus any
 * approved PTO booked against it. Activity % stays a ratio of tracked time only
 * -- neither manual entries nor PTO carry a keyboard/mouse signal, so folding
 * them in would dilute the score.
 *
 * Holidays are reported but deliberately NOT added to any total: a member who
 * worked the holiday already had that time credited as PTO by the server's
 * accumulatePTO, so adding the paid hours on top would double-count them.
 *
 * `dates` and each PTO row's `date` are 'MM/DD/YYYY' strings; activity and
 * manual timestamps are UTC. PTO rows arrive already split per day by the
 * server, one row per (request, day), and `holidayData` is keyed 'YYYY-MM-DD'.
 */
// eslint-disable-next-line import/prefer-default-export
export const buildWeekTotals = (
  activities,
  dates,
  timezone,
  manualData = [],
  offTimeData = [],
  holidayData = {},
) => {
  const times = [];
  const avgActivities = [];
  const manualTimes = [];
  const ptoTimes = [];
  const ptoData = [];
  // Holidays land on their own calendar date for everyone assigned to them --
  // the timezone picker rebuckets activity, it does not move a holiday.
  const holidayFlags = [];
  const holidayNames = [];
  const holidayZones = [];
  const holidayTimes = [];
  let totalSecondsElapsed = 0;
  let totalActiveSeconds = 0;
  let totalManualTime = 0;
  let totalPtoTime = 0;

  (dates || []).forEach((day) => {
    const startDay = moment.tz(day, 'MM/DD/YYYY', timezone).startOf('day').utc();
    const endDay = moment.tz(day, 'MM/DD/YYYY', timezone).endOf('day').utc();

    const dailyManualTime = (manualData || []).reduce((acc, manual) => {
      const manualStart = moment.utc(manual.startTime);
      if (manualStart.isSameOrAfter(startDay) && manualStart.isSameOrBefore(endDay)) {
        return acc + (manual.durationSeconds || 0);
      }
      return acc;
    }, 0);

    // Summed, not first-match: a day can carry more than one approved request
    // (a half day against two policies, say), and the column has to agree with
    // the week total either way.
    const dailyPtoTime = (offTimeData || []).reduce((acc, pto) => {
      if (moment.utc(pto.date, 'MM/DD/YYYY').isSame(moment.utc(day, 'MM/DD/YYYY'), 'day')) {
        return acc + (Number(pto.totalHours || 0) * SECONDS_PER_PTO_UNIT);
      }
      return acc;
    }, 0);

    const dayHolidays = (holidayData || {})[moment(day, 'MM/DD/YYYY').format('YYYY-MM-DD')] || [];
    holidayFlags.push(dayHolidays.length > 0);
    holidayNames.push(dayHolidays.map((h) => h.name).join(', '));
    holidayZones.push(dayHolidays.map((h) => h.timezone).join(', '));
    holidayTimes.push(dayHolidays.map((h) => String(h.paidHours || '')).filter(Boolean).join(', '));

    const dateActivity = (activities || []).reduce(
      (acc, activity) => {
        const compareDay = moment.utc(activity.startDate);
        if (compareDay.isSameOrAfter(startDay) && compareDay.isSameOrBefore(endDay)) {
          return {
            secondsElapsed: acc.secondsElapsed + activity.secondsElapsed,
            activeSeconds: acc.activeSeconds + activity.activeSeconds,
          };
        }
        return { ...acc };
      },
      { secondsElapsed: 0, activeSeconds: 0 },
    );

    totalSecondsElapsed += dateActivity.secondsElapsed;
    totalActiveSeconds += dateActivity.activeSeconds;
    totalManualTime += dailyManualTime;
    totalPtoTime += dailyPtoTime;

    times.push(moment.utc(
      (dateActivity.secondsElapsed + dailyManualTime + dailyPtoTime) * 1000,
    ).format('H:mm:ss'));
    manualTimes.push(moment.utc(dailyManualTime * 1000).format('H:mm:ss'));
    ptoTimes.push(moment.utc(dailyPtoTime * 1000).format('H:mm:ss'));
    ptoData.push(dailyPtoTime > 0);

    const avgActivity = dateActivity.secondsElapsed
      ? Number((((dateActivity.activeSeconds / dateActivity.secondsElapsed) || 0) * 100).toFixed(0))
      : null;
    avgActivities.push(avgActivity);
  });

  return {
    times,
    avgActivities,
    totalTime: convertSecToTimeFormat(totalSecondsElapsed + totalManualTime + totalPtoTime),
    totalAvgActivity: totalSecondsElapsed
      ? Number((((totalActiveSeconds / totalSecondsElapsed) || 0) * 100).toFixed(0))
      : null,
    manualTimes,
    totalManualTime: moment.utc(totalManualTime * 1000).format('H:mm:ss'),
    ptoTimes,
    ptoData,
    totalPtoTime: moment.utc(totalPtoTime * 1000).format('H:mm:ss'),
    holidayFlags,
    holidayNames,
    holidayZones,
    holidayTimes,
  };
};
